var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix4, Mesh, Object3D } from "three";
import { assert, evictMaterial } from "../../../utils";
import { readBatchModel } from "../Batch3DModel/readBatchModel";
import { MathUtils } from "../../../math";
import { ObjectType } from "../../../picking";
import { OptionsManager } from "../../../options";
export class GLTFModel extends Object3D {
    constructor(materialRegistry, pickingManager, objectId) {
        super();
        this.tileCost = OptionsManager.GLTF_MODEL_TILE_WEIGHT;
        this.credits = [];
        this.materialRegistry = materialRegistry;
        this.pickingManager = pickingManager;
        this.objectId = objectId;
        this.styleCallbackFn = (v) => this.styleCallback(v);
        this.clippingPlaneCallbackFn = (v) => this.clippingPlaneCallback(v);
        this.invalidateCallbackFn = () => this.invalidateCallback();
        materialRegistry.styleCallbacks.add(this.styleCallbackFn);
        materialRegistry.clippingPlaneCallbacks.add(this.clippingPlaneCallbackFn);
        materialRegistry.invalidateCallbacks.add(this.invalidateCallbackFn);
    }
    static create(tile, data, styleDesc, materialRegistry, pickingManager, owner) {
        return __awaiter(this, void 0, void 0, function* () {
            // View of array buffer for reading
            const view = data;
            // Generate a new ID for this object
            const objectId = pickingManager.newId();
            // Check if the file header is valid ("gltf"). If not then we aren't reading a gltf file and should throw
            assert(GLTFModel.isValidHeader(view));
            let uri;
            if (tile.content.uri !== undefined && tile.content.uri !== null) {
                uri = tile.content.uri;
            }
            else if (tile.content.url !== undefined && tile.content.url !== null) {
                uri = tile.content.url;
            }
            else {
                throw new Error("Missing uri or url in content");
            }
            // The geometry will be loaded differently depending on what extensions the point cloud uses.
            const gltf = yield readBatchModel(data, `${uri}/../`, {
                objectId: objectId || 0,
                clippingPlanes: materialRegistry.clippingPlanes,
            });
            const object = new GLTFModel(materialRegistry, pickingManager, objectId);
            object.add(gltf.scene);
            // Set the credits attached to this object
            if (gltf.asset && gltf.asset.copyright) {
                object.credits.push(...gltf.asset.copyright.split(";"));
            }
            // Apply the style definition for the first time
            object.styleCallback(styleDesc);
            // The GLTF2 loader clones the material so we need to force the children to share the same reference to the clipping planes
            object.traverse((v) => {
                var _a;
                if (v instanceof Mesh) {
                    const material = v.material;
                    material.clippingPlanes = (_a = materialRegistry.clippingPlanes) !== null && _a !== void 0 ? _a : [];
                    material.needsUpdate = true;
                }
            });
            GLTFModel.resolveTransforms(object, gltf, tile);
            // Register the object into the picking manager
            pickingManager.setForId(object.objectId, {
                objectType: ObjectType.Composite,
                rootObject: owner,
                subObject: object,
            });
            pickingManager.setIdForObject(object.id, object.objectId);
            return object;
        });
    }
    static isValidHeader(view) {
        if (view.getUint8(0) !== 103)
            return false; // g
        if (view.getUint8(1) !== 108)
            return false; // l
        if (view.getUint8(2) !== 84)
            return false; // T
        if (view.getUint8(3) !== 70)
            return false; // F
        return true;
    }
    static resolveTransforms(object, gltf, tile) {
        var _a;
        const rotation = new Matrix4();
        switch ((_a = tile.extras.owner.asset.gltfUpAxis) !== null && _a !== void 0 ? _a : "Y") {
            case "X":
                // TODO: this
                break;
            case "Y":
                rotation.makeRotationAxis(MathUtils.X_AXIS, Math.PI / 2);
                break;
            case "Z":
                break;
        }
        // RTC_CENTER is applied first, so translate by the value it holds
        const translation = new Matrix4();
        if (gltf.json !== undefined) {
            if (typeof gltf.json === "object" && gltf.json.extensionsUsed !== undefined) {
                const extensionsUsed = gltf.json.extensionsUsed;
                const used = extensionsUsed.indexOf("CESIUM_RTC") !== -1;
                if (used) {
                    const rtcCenter = gltf.json.extensions.CESIUM_RTC.center;
                    translation.makeTranslation(rtcCenter[0], rtcCenter[1], rtcCenter[2]);
                }
            }
        }
        // Lastly we recursively apply the parent transforms until we reach the root. localTransform holds the result
        // of doing this to the parent tile so we only need to do a single matrix multiply here
        const localTransform = tile.extras.transform;
        const final = localTransform.clone().multiply(translation.multiply(rotation));
        // Decompose the matrix into the object's position, rotation and scale as these are the authoritative source of
        // the object's transform
        final.decompose(object.position, object.quaternion, object.scale);
    }
    evict() {
        this.materialRegistry.styleCallbacks.delete(this.styleCallbackFn);
        this.materialRegistry.clippingPlaneCallbacks.delete(this.clippingPlaneCallbackFn);
        this.materialRegistry.invalidateCallbacks.delete(this.invalidateCallbackFn);
        if (this.objectId !== 0 && this.pickingManager !== undefined) {
            this.pickingManager.removeForId(this.objectId);
            this.pickingManager.removeForObject(this.id);
        }
        GLTFModel.evictGltf(this);
    }
    static evictGltf(object) {
        object.traverse((v) => {
            if (v instanceof Mesh) {
                v.geometry.dispose();
                if (Array.isArray(v.material)) {
                    for (const material of v.material) {
                        evictMaterial(material);
                    }
                }
                else {
                    evictMaterial(v.material);
                }
            }
        });
    }
    styleCallback(style) {
        this.traverse((v) => {
            var _a;
            if (v instanceof Mesh) {
                const material = v.material;
                material.needsUpdate = true;
                material.opacity = (_a = style.opacity) !== null && _a !== void 0 ? _a : 1;
                if (material.opacity < 1) {
                    material.transparent = true;
                    material.depthWrite = false;
                }
                else {
                    material.transparent = false;
                    material.depthWrite = true;
                }
            }
        });
    }
    clippingPlaneCallback(planes) {
        this.traverse((v) => {
            if (v instanceof Mesh) {
                const material = v.material;
                material.clippingPlanes = planes;
                material.needsUpdate = true;
            }
        });
    }
    invalidateCallback() {
        this.traverse((v) => {
            if (v instanceof Mesh) {
                const material = v.material;
                material.needsUpdate = true;
            }
        });
    }
}
